import { ALERT_TYPES, ORDER_HELP_FLOW_STEPS, ORDER_ISSUE_TYPES, REFUND_REFUSAL_REASONS } from '@nandosaus/constants';
import { get } from 'lodash';
import { observer } from 'mobx-react';
import { useRootStore } from '@nandosaus/state-management';

import { Alert } from '../../alert';
import { Box } from '../../grid';
import { Form } from '../../form';
import { FormikTextareaInput } from '../../input';
import { P } from '../../typography';
import { useSchema } from '../../../hooks/use-schema';

const getPlaceholderText = ({ isRefunded, orderIssue }) => {
  if (isRefunded) {
    return 'What do you need?';
  }

  if (orderIssue === ORDER_ISSUE_TYPES.WRONG_OR_MISSING_ITEM) {
    return 'What did we miss?';
  }

  if (orderIssue === ORDER_ISSUE_TYPES.NEVER_ARRIVED) {
    return 'Add info on what you ordered, the date/time and the restaurant location.';
  }

  return 'What did we get wrong?';
};

const AddNote = observer(() => {
  const { OrderHelpStore } = useRootStore();

  const { orderIssue, order } = OrderHelpStore;
  const validationSchema = useSchema('orderHelpAddNote');
  const isRefunded = get(order, 'isRefunded');

  const SUBHEADER = {
    [ORDER_ISSUE_TYPES.WRONG_OR_MISSING_ITEM]: (
      <P variant={3} mb={1}>
        We’re on the case. Please share some details so we know how to help.
      </P>
    ),
    [ORDER_ISSUE_TYPES.NEVER_ARRIVED]: (
      <P variant={3} mb={1}>
        Oh no! We’re so sorry about that. Please share some details so we can chase up your missing order.
      </P>
    ),
    [ORDER_ISSUE_TYPES.QUALITY]: (
      <P variant={3}>We’re on the case. Please share some details so we know how to help.</P>
    ),
    [ORDER_ISSUE_TYPES.DIETARY_REQUIREMENTS_NOT_MET]: (
      <P variant={3} mb={1}>
        Please share some more details so we know how to help.
      </P>
    ),
    [ORDER_ISSUE_TYPES.OTHER]: (
      <>
        <P variant={3} mb={1}>
          Sorry we didn’t meet your expectations this time. We’d like to make it up to you.
        </P>
        <P variant={3} mb={1}>
          Please share some details so we know how to help.
        </P>
      </>
    ),
  };

  return (
    <>
      <Form
        buttonText={OrderHelpStore.isSupportTicketFlow ? 'submit' : 'next'}
        validationSchema={validationSchema}
        initialValues={{ notes: '' }}
        topPadding={0}
        bottomPadding={1}
        onSubmit={async ({ notes }) => {
          OrderHelpStore.setNotes(notes);

          if (OrderHelpStore.isSupportTicketFlow) {
            await OrderHelpStore.createTicket();
            if (!OrderHelpStore.error) {
              OrderHelpStore.goForward();
            }

            return;
          }

          await OrderHelpStore.validateOrderRefund();

          if (OrderHelpStore.error) {
            return;
          }

          const orderInvalidForRefund = OrderHelpStore.refusalReasonCode === REFUND_REFUSAL_REASONS.INVALID;
          if (orderInvalidForRefund) {
            return;
          }

          const automatedRefundNotAvailable =
            !OrderHelpStore.success && OrderHelpStore.refusalReasonCode !== REFUND_REFUSAL_REASONS.INVALID;

          if (automatedRefundNotAvailable) {
            // go directly to end of flow, since zendesk ticket has been created
            OrderHelpStore.setCurrentStep(ORDER_HELP_FLOW_STEPS.CONFIRMATION);

            return;
          }

          const automatedRefundAvailable = OrderHelpStore.success;

          if (automatedRefundAvailable) {
            OrderHelpStore.goForward();
          }
        }}
        formBackgroundColor="transparent"
        noXPadding
        isFullWidthButton={false}
        color="greyPrimary"
      >
        {isRefunded ? (
          <P variant={3} mb={1}>
            Got your refund but still need some help? Share the details here and we’ll take care of it.
          </P>
        ) : (
          SUBHEADER[OrderHelpStore.orderIssue]
        )}
        <Box mb={1}>
          <FormikTextareaInput
            placeholder={getPlaceholderText({ orderIssue, isRefunded })}
            name="notes"
            rows={4}
            mb={1}
            maxLength={1000}
          />
        </Box>
      </Form>
      {OrderHelpStore.error && (
        <Box pb={1.5}>
          <Alert type={ALERT_TYPES.ERROR}>An error occurred submitting your request</Alert>
        </Box>
      )}
      {OrderHelpStore.refusalReasonCode === REFUND_REFUSAL_REASONS.INVALID ? (
        <Box pb={1.5}>
          <Alert type={ALERT_TYPES.ERROR}>This order is not available for refund</Alert>
        </Box>
      ) : null}
    </>
  );
});

export { AddNote };
